import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { AlertTriangle, Loader } from 'lucide-react';
import { velocityApi } from '../../services/api';
import { velocityErrorMessage } from '../../utils/velocity';

/**
 * Cancel shipment — sirf pickup se pehle.
 * reship=false → order CANCELLED. reship=true → order wapas VERIFIED, courier
 * aur AWB clear, taaki dobara (kisi aur carrier se) ship kiya ja sake.
 */
const VelocityCancelModal = ({ open, order, defaultReship = false, onClose, onCancelled }) => {
  const orderId = order?.id;
  const [reason, setReason] = useState('');
  const [reship, setReship] = useState(defaultReship);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setReship(defaultReship);
    setBusy(false);
    setError(null);
  }, [open, defaultReship]);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const shipment = await velocityApi.cancel(orderId, reason.trim(), reship);
      onCancelled?.(shipment, reship);
      onClose?.();
    } catch (err) {
      setError(velocityErrorMessage(err, { action: 'cancel' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={reship ? '🔁 Cancel & reship' : '✖ Cancel shipment'}
      open={open}
      onCancel={() => !busy && onClose?.()}
      footer={null}
      width={480}
      closable={!busy}
      maskClosable={!busy}
      destroyOnClose
    >
      <div className="space-y-4">
        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <span>Velocity cancels asynchronously. The status stays "Cancelling…" until the courier confirms.</span>
        </div>

        <label className="block text-xs font-semibold text-gray-600">
          Reason
          <textarea
            className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Customer refused"
            disabled={busy}
            maxLength={250}
          />
        </label>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-1"
            checked={reship}
            onChange={(e) => setReship(e.target.checked)}
            disabled={busy}
          />
          <span>
            Ship again after cancelling
            <span className="block text-xs text-gray-500">
              Order goes back to VERIFIED with the courier and AWB cleared. Leave this off to cancel the order.
            </span>
          </span>
        </label>

        {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="px-5 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 disabled:opacity-50"
            onClick={onClose}
            disabled={busy}
          >
            Keep shipment
          </button>
          <button
            type="button"
            className="px-6 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            onClick={submit}
            disabled={busy}
          >
            {busy && <Loader className="w-4 h-4 animate-spin" />}
            {reship ? 'Cancel & reship' : 'Cancel shipment'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default VelocityCancelModal;
